import { API_BASE_URL } from "@/config";
import { validateToken } from '@/utils/auth';
import Swal from "sweetalert2";

import { pdf_decryptify } from "@/utils/pdf_management.js";

export default {
  name: "SupportingDocuments",
  emits: ["validity-change", "previous"],
  data: () => ({
    applicant_type: "Individual", // "Individual" or "Company"
    id_type: "", // "ID Book" or "ID Card"
    selected_files: {
      IDBOOK: [],
      IDCARD_FRONT: [],
      IDCARD_BACK: [],
      DRIVERSLICENSE_FRONT: [],
      DRIVERSLICENSE_BACK: [],
      BANKSTATEMENTS: [],
      PAYSLIP: [],
      PROOFOFRESIDENCE: [],
      MARRIAGECERTIFICATE: [],
      COMPANYREGISTRATION: [],
    },
    existing_documents: {
      IDBOOK: [],
      IDCARD_FRONT: [],
      IDCARD_BACK: [],
      DRIVERSLICENSE_FRONT: [],
      DRIVERSLICENSE_BACK: [],
      BANKSTATEMENTS: [],
      PAYSLIP: [],
      PROOFOFRESIDENCE: [],
      MARRIAGECERTIFICATE: [],
      COMPANYREGISTRATION: [],
    },
    error_messages: {
      BANKSTATEMENTS: "",
      COMPANYREGISTRATION: "",
      DRIVERSLICENSE: "",
      IDBOOK: "",
      IDCARD_FRONT: "",
      IDCARD_BACK: "",
      MARRIAGECERTIFICATE: "",
      PAYSLIP: "",
      PROOFOFRESIDENCE: "",
    },
    is_dragging: {
      BANKSTATEMENTS: false,
      COMPANYREGISTRATION: false,
      DRIVERSLICENSE: false,
      IDBOOK: false,
      IDCARD_FRONT: false,
      IDCARD_BACK: false,
      DRIVERSLICENSE_FRONT: false,
      DRIVERSLICENSE_BACK: false,
      MARRIAGECERTIFICATE: false,
      PAYSLIP: false,
      PROOFOFRESIDENCE: false,
    },
    max_files: {
      BANKSTATEMENTS: 3,
      COMPANYREGISTRATION: 1,
      DRIVERSLICENSE: 1,
      IDBOOK: 1,
      IDCARD_FRONT: 1,
      IDCARD_BACK: 1,
      DRIVERSLICENSE_FRONT: 1,
      DRIVERSLICENSE_BACK: 1,
      MARRIAGECERTIFICATE: 1,
      PAYSLIP: 3,
      PROOFOFRESIDENCE: 1,
    },
    is_loading: false,
    workflow_id: null,
    is_married: false,
    is_employed: false,
    has_bank_statements: false,
  }),
  computed: {
    all_files() {
      const combined = {};
      Object.keys(this.selected_files).forEach((key) => {
        combined[key] = [
          ...this.existing_documents[key],
          ...this.selected_files[key],
        ];
      });
      return combined;
    },
    all_required_filled() {
      // Ensure id_type is selected
      if (this.id_type !== "ID Book" && this.id_type !== "ID Card") {
        return false;
      }

      // Ensure at least one of ID Book or ID Card (both front and back) is uploaded
      const hasValidIdCard =
        this.id_type === "ID Card" &&
        this.all_files.IDCARD_FRONT.length === 1 &&
        this.all_files.IDCARD_BACK.length === 1;

      const hasValidIdBook =
        this.id_type === "ID Book" && this.all_files.IDBOOK.length === 1;

      if (!hasValidIdBook && !hasValidIdCard) {
        return false;
      }

      // Ensure Driver's License (both front and back) is uploaded
      if (
        this.all_files.DRIVERSLICENSE_FRONT.length !== 1 ||
        this.all_files.DRIVERSLICENSE_BACK.length !== 1
      ) {
        return false;
      }

      if (this.has_bank_statements == false && this.all_files.BANKSTATEMENTS.length < 1)
        return false;
      if (this.applicant_type === "Company" && this.all_files.COMPANYREGISTRATION.length < 1)
        return false;
      if (this.is_married && this.all_files.MARRIAGECERTIFICATE.length < 1)
        return false;
      if (this.is_employed && this.all_files.PAYSLIP.length < 1)
        return false;
      if (this.all_files.PROOFOFRESIDENCE.length < 1)
        return false;
      return true;
    },
  },
  methods: {
    pdf_decryptify,
    previous() {
      this.$emit("previous");
    },
    async create_file_from_base_64(base64String, fileName, documentType) {
      try {
        // Decode base64 string to binary data
        const byteString = atob(base64String.split(",")[1] || base64String);
        const ab = new ArrayBuffer(byteString.length);
        const ia = new Uint8Array(ab);
        for (let i = 0; i < byteString.length; i++) {
          ia[i] = byteString.charCodeAt(i);
        }
        // Try to infer file type from fileName extension
        let type = "application/octet-stream";
        if (fileName.endsWith(".pdf")) type = "application/pdf";
        else if (fileName.endsWith(".jpg") || fileName.endsWith(".jpeg"))
          type = "image/jpeg";
        else if (fileName.endsWith(".png")) type = "image/png";

        const file = new File([ab], fileName, {
          type: type,
          lastModified: Date.now(),
        });
        file.document_id = documentType.document_id;
        file.is_existing = true;
        file.document_url = null; // No direct URL, since it's base64
        return file;
      } catch (error) {
        console.error("Error creating file from base64:", error);
        return null;
      }
    },
    async fetch_documents() {
      const token = localStorage.getItem("token");
      const upload_api = `${API_BASE_URL}/verify_api/financial_information_api/get_uploaded_documents/`;

      // Show loading indicator
      Swal.fire({
        title: "Loading documents",
        text: "Please wait...",
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      try {
        const response = await $.ajax({
          type: "POST",
          url: upload_api,
          headers: { Authorization: `Token ${token}` },
          data: JSON.stringify({
            workflow_id: this.workflow_id,
          }),
          contentType: "application/json",
        });

        // Close loading indicator
        Swal.close();

        if (response.status === "success" && response.data) {
          const documents = Array.isArray(response.data) ? response.data : [];

          // Reset existing documents
          Object.keys(this.existing_documents).forEach((key) => {
            this.existing_documents[key] = [];
          });

          // Process each document
          for (const doc of documents) {
            const documentType = doc.document_type;

            if (documentType === "BANKSTATEMENTS") {
              if (!doc.supporting_document) {
                this.has_bank_statements = true;
              }
            }

            if (documentType === "IDBOOK") {
              if (this.id_type === "") {
                this.id_type = "ID Book";
              }
            }

            if (documentType === "IDCARD") {
              if (this.id_type === "") {
                this.id_type = "ID Card";
              }
            }

            if (documentType === "BANKSTATEMENTS") {
              if (doc.supporting_document) {
                // Extract filename from URL or create a default name
                const urlParts = doc.document_path.split("/");
                const fileName =
                  urlParts[urlParts.length - 1].split("?")[0] ||
                  `${documentType.toLowerCase()}_document`;

                // Create file object from base64 string
                const fileObj = await this.create_file_from_base_64(
                  doc.document_base_64,
                  fileName,
                  doc
                );

                if (fileObj && this.existing_documents[documentType]) {
                  this.existing_documents[documentType].push(fileObj);
                }
              }
            } else {
              // Extract filename from URL or create a default name
              const urlParts = doc.document_path.split("/");
              const fileName =
                urlParts[urlParts.length - 1].split("?")[0] ||
                `${documentType.toLowerCase()}_document`;

              // Create file object from base64 string
              const fileObj = await this.create_file_from_base_64(
                doc.document_base_64,
                fileName,
                doc
              );

              if (fileObj && this.existing_documents[documentType]) {
                this.existing_documents[documentType].push(fileObj);
              }
            }
          }
        }
      } catch (error) {
        // Close loading indicator and show error message
        Swal.close();
        this.has_bank_statements = false;
        console.error("Error fetching documents:", error);
      }
    },
    fetch_sygnio_answers() {
      const token = localStorage.getItem("token");
      const service_api = `${API_BASE_URL}/verify_api/financial_information_api/get_sygnio_question_answers/`;

      $.ajax({
        type: "POST",
        url: service_api,
        headers: {
          Authorization: `Token ${token}`,
        },
        data: JSON.stringify({ workflow_id: this.workflow_id }),
        contentType: "application/json",
        success: (response) => {
          if (response.status === "success" && response.data) {
            const data = response.data;
            if (Array.isArray(data)) {
              data.forEach((item) => {
                const key = item.api_form_field;
                const value = item.answer;
                if (key === "customerMaritalStatus") {
                  if (value === "Married") {
                    this.is_married = true;
                  } else {
                    this.is_married = false;
                  }
                } else if (key === "customerEmploymentStatus") {
                  var hides = [
                    "Not Selected",
                    "Student",
                    "Self employed",
                    "Pensioner",
                    "Unemployed",
                  ];
                  if (!hides.includes(value)) {
                    this.is_employed = true;
                  } else {
                    this.is_employed = false;
                  }
                }
              });
            } else if (typeof data === "object") {
              Object.keys(data).forEach((key) => {
                const value = data[key];
                if (key === "customerMaritalStatus") {
                  if (value === "Married") {
                    this.is_married = true;
                  } else {
                    this.is_married = false;
                  }
                } else if (key === "customerEmploymentStatus") {
                  var hides = [
                    "Not Selected",
                    "Student",
                    "Self employed",
                    "Pensioner",
                    "Unemployed",
                  ];
                  if (!hides.includes(value)) {
                    this.is_employed = true;
                  } else {
                    this.is_employed = false;
                  }
                }
              });
            }
          }
        },
        error: () => {
          Swal.fire({
            title: "Error",
            text: "Failed to fetch the answers, please try again later.",
            icon: "error",
            timer: 3000,
            timerProgressBar: true,
            showConfirmButton: false,
          });
        },
      });
    },
    get_label(doc) {
      const labels = {
        BANKSTATEMENTS: "Bank Statements - 3 Months",
        COMPANYREGISTRATION: "Company Registration",
        DRIVERSLICENSE_FRONT: "Driver's License (Front)",
        DRIVERSLICENSE_BACK: "Driver's License (Back)",
        IDBOOK: "ID Book Copy",
        IDCARD_FRONT: "ID Card (Front)",
        IDCARD_BACK: "ID Card (Back)",
        MARRIAGECERTIFICATE: "Marriage Certificate",
        PAYSLIP: "Payslip - 3 Months",
        PROOFOFRESIDENCE: "Proof of Residence",
      };
      return labels[doc] || doc;
    },
    should_show_section(doc) {
      if (doc === "COMPANYREGISTRATION" && this.applicant_type !== "Company")
        return false;
      if (doc === "IDBOOK" && this.id_type !== "ID Book") return false;
      if ((doc === "IDCARD_FRONT" || doc === "IDCARD_BACK") && this.id_type !== "ID Card") return false;

      // Show Bank Statements section only if they don't have bank statements yet
      if (doc === "BANKSTATEMENTS" && this.has_bank_statements) return false;

      // Hide Marriage Certificate if not married
      if (doc === "MARRIAGECERTIFICATE" && !this.is_married) return false;
      // Hide Payslip if not employed
      if (doc === "PAYSLIP" && !this.is_employed) return false;
      return true;
    },
    is_required_document(doc) {
      // Required documents based on the validation logic
      const always_required = [
        "IDBOOK", "IDCARD_FRONT", "IDCARD_BACK",
        "DRIVERSLICENSE_FRONT", "DRIVERSLICENSE_BACK",
        "PROOFOFRESIDENCE"
      ];
      
      if (always_required.includes(doc)) return true;
      if (doc === "BANKSTATEMENTS" && !this.has_bank_statements) return true;
      if (doc === "MARRIAGECERTIFICATE" && this.is_married) return true;
      if (doc === "PAYSLIP" && this.is_employed) return true;
      if (doc === "COMPANYREGISTRATION" && this.applicant_type === "Company") return true;
      
      return false;
    },
    get_max_files(doc) {
      return this.max_files[doc] || 1;
    },
    async handle_file_change(event, type) {
      if (event.target.files && event.target.files.length > 0) {
        let files_returned = Array.from(event.target.files);
        const processed_files = [];
        for (const file of files_returned) {
            try {
                if (file.type == "application/pdf") {
                    const result = await this.pdf_decryptify(file);
                    if (!result || typeof result === "string" || !result.type) {
                      continue;
                    }
                    processed_files.push(result);
                }
                else {
                    processed_files.push(file);
                }
            } catch (err) {
                console.error('Error processing file:', err);
                continue;
            }
        }
        event.target.value = "";
        
        let new_files = processed_files;
        if (new_files.length === 0) {
          return;
        }
        const current_total = this.all_files[type].length;

        if (current_total + new_files.length > this.max_files[type]) {
          this.error_messages[
            type
          ] = `You can upload a maximum of ${this.max_files[type]} files`;
          return;
        }

        const valid_types = [
          "application/pdf",
          "image/jpeg",
          "image/png",
          "image/jpg",
        ];
        const invalid_files = new_files.filter(
          (file) => !valid_types.includes(file.type)
        );
        if (invalid_files.length > 0) {
          this.error_messages[type] =
            "Some files have invalid types. Please upload only PDF, JPEG, PNG, or JPG files.";
          return;
        }
        const oversized = new_files.filter(
          (file) => file.size > 50 * 1024 * 1024
        );
        if (oversized.length > 0) {
          this.error_messages[type] =
            "Some files exceed the maximum size limit of 50MB.";
          return;
        }
        this.selected_files[type] = [
          ...this.selected_files[type],
          ...new_files,
        ];
        this.error_messages[type] = "";
      }
    },
    async handle_file_drop(event, type) {
      this.is_dragging[type] = false;
      const files = event.dataTransfer.files;
      if (files && files.length > 0) {
        let files_returned = Array.from(files);

        const processed_files = [];
        for (const file of files_returned) {
          try {
              if (file.type == "application/pdf") {
                  const result = await this.pdf_decryptify(file);
                  if (!result || typeof result === "string" || !result.type) {
                    continue;
                  }
                  processed_files.push(result);
              } else {
                  processed_files.push(file);
              }
          } catch (err) {
              console.error('Error processing file:', err);
              continue;
          }
        }
        let new_files = processed_files;
        if (new_files.length === 0) {
          return;
        }
        const current_total = this.all_files[type].length;

        if (current_total + new_files.length > this.max_files[type]) {
          this.error_messages[
            type
          ] = `You can upload a maximum of ${this.max_files[type]} files`;
          return;
        }

        const valid_types = [
          "application/pdf",
          "image/jpeg",
          "image/png",
          "image/jpg",
        ];
        const valid_files = new_files.filter((file) =>
          valid_types.includes(file.type)
        );
        if (valid_files.length === 0) {
          this.error_messages[type] =
            "Invalid file type(s). Please upload PDF, JPEG, PNG, or JPG files.";
          return;
        }
        const valid_sized = valid_files.filter(
          (file) => file.size <= 50 * 1024 * 1024
        );
        if (valid_sized.length === 0) {
          this.error_messages[type] =
            "All files exceed the maximum size limit of 50MB.";
          return;
        }
        if (valid_sized.length !== new_files.length) {
          this.error_messages[type] =
            "Some files were skipped because they exceed the 50MB size limit or invalid type.";
        }
        this.selected_files[type] = [
          ...this.selected_files[type],
          ...valid_sized,
        ];
      }
    },
    remove_file(type, index) {
      const allFiles = this.all_files[type];
      const fileToRemove = allFiles[index];

      if (fileToRemove.is_existing) {
        // Remove from existing documents
        const existingIndex = this.existing_documents[type].findIndex(
          (f) => f.document_id === fileToRemove.document_id
        );
        if (existingIndex > -1) {
          this.existing_documents[type].splice(existingIndex, 1);
        }
      } else {
        // Remove from selected files
        const selectedIndex = index - this.existing_documents[type].length;
        if (selectedIndex >= 0) {
          this.selected_files[type].splice(selectedIndex, 1);
        }
      }
    },
    view_file(file) {
      if (file.is_existing && file.document_url) {
        // Open existing document in new tab
        window.open(file.document_url, "_blank");
      } else {
        // Create object URL for new files
        const url = URL.createObjectURL(file);
        window.open(url, "_blank");
        // Clean up the object URL after a delay
        setTimeout(() => URL.revokeObjectURL(url), 1000);
      }
    },
    format_file_size(bytes) {
      if (bytes < 1024) return bytes + " bytes";
      if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + " KB";
      return (bytes / (1024 * 1024)).toFixed(2) + " MB";
    },
    // Helper method to prepare files for API
    prepare_files_for_api() {
      const prepared_files = { ...this.selected_files };

      // Combine ID Card front and back
      if (this.id_type === "ID Card") {
        prepared_files.id_card = [
          ...this.all_files.IDCARD_FRONT,
          ...this.all_files.IDCARD_BACK,
        ];
        delete prepared_files.IDCARD_FRONT;
        delete prepared_files.IDCARD_BACK;
        delete prepared_files.IDBOOK;
      } else {
        prepared_files.id_book = this.all_files.IDBOOK;
        delete prepared_files.IDCARD_FRONT;
        delete prepared_files.IDCARD_BACK;
      }

      // Combine Driver's License front and back
      prepared_files.drivers_license = [
        ...this.all_files.DRIVERSLICENSE_FRONT,
        ...this.all_files.DRIVERSLICENSE_BACK,
      ];
      delete prepared_files.DRIVERSLICENSE_FRONT;
      delete prepared_files.DRIVERSLICENSE_BACK;

      // Map remaining fields to API format
      prepared_files.bank_statements = prepared_files.BANKSTATEMENTS;
      prepared_files.company_registration = prepared_files.COMPANYREGISTRATION;
      prepared_files.payslip = prepared_files.PAYSLIP;
      prepared_files.proof_of_residence = prepared_files.PROOFOFRESIDENCE;
      prepared_files.marriage_certificate = prepared_files.MARRIAGECERTIFICATE;

      // Remove old keys
      delete prepared_files.BANKSTATEMENTS;
      delete prepared_files.COMPANYREGISTRATION;
      delete prepared_files.PAYSLIP;
      delete prepared_files.PROOFOFRESIDENCE;
      delete prepared_files.MARRIAGECERTIFICATE;

      return prepared_files;
    },

    async upload_documents() {
      if (!this.all_required_filled) {
        Swal.fire({
          icon: "warning",
          title: "Validation",
          text: "Please upload all required documents",
          timer: 3000,
          timerProgressBar: true,
        });
        return;
      }

      // 🚨 Check total file size before sending
      const allFiles = Object.values(this.all_files).flat();
      const totalSizeBytes = allFiles.reduce((sum, file) => sum + file.size, 0);
      const maxSizeBytes = 50 * 1024 * 1024;

      if (totalSizeBytes > maxSizeBytes) {
        Swal.fire({
          icon: "error",
          title: "Upload Limit Exceeded",
          text: "Total file size exceeds 50MB. Please remove some files.",
          timer: 3000,
          timerProgressBar: true,
        });
        return;
      }

      this.is_loading = true;

      Swal.fire({
        title: "Saving documents",
        text: "Please wait...",
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      const token = localStorage.getItem("token");
      const upload_api = `${API_BASE_URL}/verify_api/financial_information_api/upload_supporting_documentation/`;
      const formData = new FormData();

      // Use the prepared files instead of selected_files directly
      const prepared_files = this.prepare_files_for_api();

      Object.entries(prepared_files).forEach(([key, files]) => {
        files.forEach((file) => {
          formData.append(key, file);
        });
      });

      formData.append("workflow_id", this.workflow_id);

      try {
        await $.ajax({
          url: upload_api,
          method: "POST",
          contentType: false,
          processData: false,
          headers: { Authorization: `Token ${token}` },
          data: formData,
        });
        Swal.close();
        this.is_loading = false;
        this.$emit("validity-change", true);

      } catch (error) {
        
        Swal.close();
        this.is_loading = false;
        
        Swal.fire({
          icon: "error",
          title: "Error",
          text:
            error.responseJSON?.message ||
            "Upload failed. Please try again later.",
          timer: 3000,
          timerProgressBar: true,
        });

      }
    },
  },
  mounted() {
    if (!validateToken(this.$router)) return;
    this.workflow_id = this.$store.getters.get_workflow_id;
    this.$nextTick(() => {
      this.fetch_documents();
      this.fetch_sygnio_answers();
    });
  },
};
